import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, Send, Users, Loader2, X, Plus, Image as ImageIcon, Trash2, Upload, Palette, Mail, CheckCircle2, FileSpreadsheet, LayoutTemplate, Pause, Play, GripVertical, UserX } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Resolve image URL: turns "/api/newsletter/image-proxy?key=..." into absolute URL
const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${BACKEND_URL}${url}`;
};

const NewsletterEditor = ({ user }) => {
  const navigate = useNavigate();
  const { newsletterId } = useParams();
  
  const [nl, setNl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [recipientsDialogOpen, setRecipientsDialogOpen] = useState(false);
  const [styleDialogOpen, setStyleDialogOpen] = useState(false);
  const [crmCustomers, setCrmCustomers] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [crmFilter, setCrmFilter] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [imgUploadLoadingFor, setImgUploadLoadingFor] = useState(null);
  const [gmailStatus, setGmailStatus] = useState({ connected: false, email: null });
  const [importingRecipients, setImportingRecipients] = useState(false);
  const [sendProgress, setSendProgress] = useState(null);
  const [aiQuota, setAiQuota] = useState({ total: 0, used: 0, remaining: 0 });
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [applyingTemplate, setApplyingTemplate] = useState(null);
  const [pauseResumeLoading, setPauseResumeLoading] = useState(false);
  const [unsubscribesDialogOpen, setUnsubscribesDialogOpen] = useState(false);
  const [unsubscribes, setUnsubscribes] = useState([]);
  const [logoUploading, setLogoUploading] = useState(false);
  const [draggedBlockId, setDraggedBlockId] = useState(null);
  const [dragOverBlockId, setDragOverBlockId] = useState(null);
  const logoUploadRef = useRef(null);
  const importInputRef = useRef(null);
  const saveTimer = useRef(null);

  const fetchNl = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/newsletter/drafts/${newsletterId}?user_id=${user.user_id}`);
      setNl(resp.data);
      setSelectedCustomerIds(resp.data.recipient_ids || []);
    } catch (e) {
      toast.error('Σφάλμα φόρτωσης');
      navigate('/newsletter');
    } finally {
      setLoading(false);
    }
  }, [newsletterId, user.user_id, navigate]);

  useEffect(() => { fetchNl(); }, [fetchNl]);

  // If newsletter is mid-sending on load, kick off progress polling
  const polledRef = useRef(false);
  useEffect(() => {
    if (nl?.status === 'sending' && !polledRef.current) {
      polledRef.current = true;
      pollSendProgress();
    }
    if (nl?.status !== 'sending') {
      polledRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nl?.status]);

  // Fetch Gmail connection status
  const fetchGmailStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/newsletter/gmail/status?user_id=${user.user_id}`);
      setGmailStatus(r.data);
    } catch (e) { /* ignore */ }
  }, [user.user_id]);

  // Fetch AI quota
  const fetchAiQuota = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/newsletter/ai-quota?user_id=${user.user_id}`);
      setAiQuota(r.data);
    } catch (e) { /* ignore */ }
  }, [user.user_id]);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/newsletter/templates`);
      setTemplates(r.data.templates || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchGmailStatus(); fetchAiQuota(); fetchTemplates(); }, [fetchGmailStatus, fetchAiQuota, fetchTemplates]);

  // Listen for popup completion message
  useEffect(() => {
    const handler = (ev) => {
      if (ev.data && ev.data.type === 'gmail-connected') {
        fetchGmailStatus();
        toast.success(`Συνδέθηκε: ${ev.data.email || 'Gmail'}`);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [fetchGmailStatus]);

  const handleConnectGmail = () => {
    const url = `${API}/newsletter/gmail/login?user_id=${user.user_id}`;
    const popup = window.open(url, 'gmail-oauth', 'width=520,height=640,scrollbars=yes');
    // Fallback: poll until popup closes, then refresh status
    const timer = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(timer);
        fetchGmailStatus();
      }
    }, 1000);
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('Αποσύνδεση του Gmail account;')) return;
    try {
      await axios.post(`${API}/newsletter/gmail/disconnect?user_id=${user.user_id}`);
      setGmailStatus({ connected: false, email: null });
      toast.success('Αποσυνδέθηκε');
    } catch (e) {
      toast.error('Σφάλμα αποσύνδεσης');
    }
  };

  const handleImportRecipients = async (file) => {
    if (!file) return;
    setImportingRecipients(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(
        `${API}/newsletter/drafts/${newsletterId}/recipients/import?user_id=${user.user_id}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setNl(prev => ({ ...prev, recipient_emails: r.data.recipient_emails }));
      toast.success(`Εισήχθησαν ${r.data.added_count} νέα emails (${r.data.imported_count} βρέθηκαν). Σύνολο: ${r.data.total_recipients}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Σφάλμα εισαγωγής');
    } finally {
      setImportingRecipients(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  // Auto-save with debounce — uses functional updates to avoid stale closures
  const scheduleSave = (updaterOrPatch) => {
    setNl(prev => {
      const next = typeof updaterOrPatch === 'function' ? updaterOrPatch(prev) : { ...prev, ...updaterOrPatch };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persistSave(next), 1000);
      return next;
    });
  };

  const persistSave = async (latest) => {
    if (!latest) return;
    setSaving(true);
    try {
      await axios.put(`${API}/newsletter/drafts/${newsletterId}?user_id=${user.user_id}`, {
        title: latest.title,
        subject: latest.subject,
        preview_text: latest.preview_text,
        blocks: latest.blocks,
        style: latest.style,
        recipient_ids: latest.recipient_ids,
        recipient_emails: latest.recipient_emails,
      });
    } catch (e) {
      toast.error('Σφάλμα αποθήκευσης');
    } finally {
      setTimeout(() => setSaving(false), 500);
    }
  };

  const updateBlock = (blockId, contentUpdates) => {
    scheduleSave(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => 
        b.block_id === blockId ? { ...b, content: { ...b.content, ...contentUpdates } } : b
      )
    }));
  };

  const removeBlock = (blockId) => {
    if (!window.confirm('Σίγουρα θέλετε να διαγράψετε αυτό το block;')) return;
    scheduleSave(prev => ({ ...prev, blocks: prev.blocks.filter(b => b.block_id !== blockId) }));
  };

  const addBlock = (type, afterIdx = null) => {
    const defaults = {
      hero: { title: 'Νέος Τίτλος', subtitle: '', image_url: '' },
      text: { heading: '', body: 'Νέο κείμενο. Πατήστε για επεξεργασία.' },
      cta: { button_text: 'Πατήστε εδώ', button_url: 'https://', button_color: nl?.style?.primary_color || '#0891b2' },
      footer: { text: 'Με εκτίμηση,', signature: '', logo_url: '' },
    };
    const newBlock = {
      block_id: `${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
      block_type: type,
      content: defaults[type] || {},
      order: 0,
    };
    scheduleSave(prev => {
      const blocks = [...prev.blocks];
      if (afterIdx === null || afterIdx >= blocks.length - 1) {
        blocks.push(newBlock);
      } else {
        blocks.splice(afterIdx + 1, 0, newBlock);
      }
      return { ...prev, blocks: blocks.map((b, i) => ({ ...b, order: i })) };
    });
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) return toast.error('Γράψτε ένα prompt');
    if (aiQuota.remaining <= 0) {
      toast.error(`Έχετε εξαντλήσει τα AI generations (${aiQuota.used}/${aiQuota.total}). Επικοινωνήστε με τον διαχειριστή.`);
      return;
    }
    setAiLoading(true);
    try {
      const resp = await axios.post(
        `${API}/newsletter/drafts/${newsletterId}/ai-generate?user_id=${user.user_id}`,
        { prompt: aiPrompt, tone: aiTone, include_image: true },
        { timeout: 90000 }
      );
      setNl(resp.data.newsletter);
      if (resp.data.ai_quota) setAiQuota(resp.data.ai_quota);
      setAiDialogOpen(false);
      setAiPrompt('');
      toast.success('Έτοιμο! Το newsletter δημιουργήθηκε με εικόνα.');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Σφάλμα AI';
      if (status === 402) toast.error(detail);
      else toast.error(detail);
      fetchAiQuota();
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplyTemplate = async (templateId) => {
    setApplyingTemplate(templateId);
    try {
      const r = await axios.post(
        `${API}/newsletter/drafts/${newsletterId}/apply-template?user_id=${user.user_id}`,
        { template_id: templateId }
      );
      setNl(r.data.newsletter);
      setTemplatesDialogOpen(false);
      toast.success(r.data.message || 'Το template εφαρμόστηκε!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Σφάλμα εφαρμογής template');
    } finally {
      setApplyingTemplate(null);
    }
  };

  const handleUploadImage = async (blockId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Παρακαλώ επιλέξτε εικόνα');
      return;
    }
    setImgUploadLoadingFor(blockId);
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await axios.post(
        `${API}/newsletter/drafts/${newsletterId}/upload-image?user_id=${user.user_id}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      updateBlock(blockId, { image_url: resp.data.image_url });
      toast.success('Εικόνα ανέβηκε!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Σφάλμα ανεβάσματος');
    } finally {
      setImgUploadLoadingFor(null);
    }
  };

  const fetchCrmCustomers = async () => {
    try {
      const resp = await axios.get(`${API}/crm/customers?user_id=${user.user_id}&limit=500`);
      const list = Array.isArray(resp.data) ? resp.data : (resp.data.customers || []);
      setCrmCustomers(list.filter(c => c.email));
    } catch (e) {
      toast.error('Σφάλμα φόρτωσης πελατών');
    }
  };

  const openRecipientsDialog = () => { fetchCrmCustomers(); setRecipientsDialogOpen(true); };
  const toggleCustomer = (id) => setSelectedCustomerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAllFiltered = () => {
    const filteredIds = filteredCustomers.map(c => c.customer_id);
    setSelectedCustomerIds(prev => Array.from(new Set([...prev, ...filteredIds])));
  };
  const deselectAll = () => setSelectedCustomerIds([]);
  const saveRecipients = () => {
    scheduleSave({ recipient_ids: selectedCustomerIds });
    setRecipientsDialogOpen(false);
    toast.success(`Επιλέχθηκαν ${selectedCustomerIds.length} παραλήπτες`);
  };
  const addManualEmail = () => {
    if (!manualEmail.includes('@')) return toast.error('Μη έγκυρο email');
    const existing = nl.recipient_emails || [];
    if (existing.includes(manualEmail)) return;
    scheduleSave({ recipient_emails: [...existing, manualEmail] });
    setManualEmail('');
  };
  const removeManualEmail = (em) => scheduleSave({ recipient_emails: (nl.recipient_emails || []).filter(x => x !== em) });

  const handleSend = async () => {
    if (!gmailStatus.connected) {
      toast.error('Συνδέστε πρώτα το Gmail σας');
      return;
    }
    if (!nl.subject || !nl.subject.trim()) {
      toast.error('Συμπληρώστε το Θέμα του email');
      return;
    }
    const totalRecip = (nl.recipient_ids?.length || 0) + (nl.recipient_emails?.length || 0);
    if (!window.confirm(`Αποστολή σε ${totalRecip} παραλήπτες με delays 50-90s; Η διαδικασία θα διαρκέσει περίπου ${Math.round((totalRecip * 70) / 60)} λεπτά.`)) return;
    try {
      const r = await axios.post(`${API}/newsletter/drafts/${newsletterId}/send?user_id=${user.user_id}`, {
        min_delay: 50,
        max_delay: 90,
      });
      toast.success(r.data.message || 'Η αποστολή ξεκίνησε');
      // Immediately refetch so Pause button renders
      await fetchNl();
      // Begin polling progress
      pollSendProgress();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Σφάλμα αποστολής';
      if (status === 412) toast.error('Συνδέστε πρώτα το Gmail σας');
      else toast.error(detail);
    }
  };

  const pollSendProgress = () => {
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/newsletter/drafts/${newsletterId}/send-progress?user_id=${user.user_id}`);
        setSendProgress(r.data);
        // Keep local nl status/counts in sync for the top-bar buttons to render correctly
        setNl(prev => prev ? ({
          ...prev,
          status: r.data.status ?? prev.status,
          sent_count: r.data.sent_count ?? prev.sent_count,
          failed_count: r.data.failed_count ?? prev.failed_count,
          total_recipients: r.data.total_recipients ?? prev.total_recipients,
        }) : prev);
        if (r.data.status === 'sending') {
          setTimeout(tick, 5000);
        } else {
          fetchNl();
          if (r.data.status === 'sent') toast.success(`Ολοκληρώθηκε! ${r.data.sent_count} στάλθηκαν.`);
          else if (r.data.status === 'partial') toast.warning(`Ολοκληρώθηκε με σφάλματα: ${r.data.sent_count} στάλθηκαν, ${r.data.failed_count} απέτυχαν.`);
          else if (r.data.status === 'failed') toast.error('Η αποστολή απέτυχε');
          else if (r.data.status === 'paused') toast.info('Η αποστολή είναι σε παύση');
        }
      } catch (e) { /* stop polling on error */ }
    };
    tick();
  };

  const handlePauseSending = async () => {
    setPauseResumeLoading(true);
    try {
      await axios.post(`${API}/newsletter/drafts/${newsletterId}/pause?user_id=${user.user_id}`);
      toast.success('Η αποστολή θα σταματήσει στο επόμενο email');
      setTimeout(() => fetchNl(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Σφάλμα παύσης');
    } finally {
      setPauseResumeLoading(false);
    }
  };

  const handleResumeSending = async () => {
    setPauseResumeLoading(true);
    try {
      const r = await axios.post(`${API}/newsletter/drafts/${newsletterId}/resume?user_id=${user.user_id}`);
      toast.success(r.data.message || 'Η αποστολή συνεχίστηκε');
      fetchNl();
      pollSendProgress();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Σφάλμα συνέχισης');
    } finally {
      setPauseResumeLoading(false);
    }
  };

  const handleUploadLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Παρακαλώ επιλέξτε εικόνα');
      return;
    }
    setLogoUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(
        `${API}/newsletter/drafts/${newsletterId}/upload-logo?user_id=${user.user_id}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setNl(prev => ({ ...prev, style: r.data.style }));
      toast.success('Logo ανέβηκε!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Σφάλμα ανεβάσματος logo');
    } finally {
      setLogoUploading(false);
      if (logoUploadRef.current) logoUploadRef.current.value = '';
    }
  };

  const fetchUnsubscribes = async () => {
    try {
      const r = await axios.get(`${API}/newsletter/unsubscribes?user_id=${user.user_id}`);
      setUnsubscribes(r.data.unsubscribes || []);
    } catch (e) {
      toast.error('Σφάλμα φόρτωσης unsubscribes');
    }
  };

  const openUnsubscribesDialog = () => { fetchUnsubscribes(); setUnsubscribesDialogOpen(true); };

  // Drag & drop block reordering
  const handleDragStart = (blockId) => setDraggedBlockId(blockId);
  const handleDragOver = (e, blockId) => {
    e.preventDefault();
    if (blockId !== draggedBlockId) setDragOverBlockId(blockId);
  };
  const handleDragEnd = () => { setDraggedBlockId(null); setDragOverBlockId(null); };
  const handleDrop = (e, targetBlockId) => {
    e.preventDefault();
    if (!draggedBlockId || draggedBlockId === targetBlockId) {
      handleDragEnd();
      return;
    }
    scheduleSave(prev => {
      const blocks = [...prev.blocks];
      const fromIdx = blocks.findIndex(b => b.block_id === draggedBlockId);
      const toIdx = blocks.findIndex(b => b.block_id === targetBlockId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = blocks.splice(fromIdx, 1);
      blocks.splice(toIdx, 0, moved);
      return { ...prev, blocks: blocks.map((b, i) => ({ ...b, order: i })) };
    });
    handleDragEnd();
  };

  const filteredCustomers = crmCustomers.filter(c => {
    if (!crmFilter) return true;
    const q = crmFilter.toLowerCase();
    return (c.company_name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  if (loading || !nl) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  const totalRecipients = (nl.recipient_ids?.length || 0) + (nl.recipient_emails?.length || 0);
  const primaryColor = nl.style?.primary_color || '#0891b2';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="bg-white border-b sticky top-0 z-20 shadow-sm">
        <div className="max-w-6xl mx-auto p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/newsletter')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Input
              value={nl.title}
              onChange={(e) => scheduleSave({ title: e.target.value })}
              className="font-semibold text-base max-w-xs border-0 focus-visible:ring-1 focus-visible:ring-pink-300 px-2"
              data-testid="nl-title-input"
            />
            {saving ? (
              <span className="text-xs text-amber-600 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Αποθήκευση...</span>
            ) : (
              <span className="text-xs text-emerald-600">✓ Αποθηκευμένο</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Gmail Status */}
            {gmailStatus.connected ? (
              <button onClick={handleDisconnectGmail} className="flex items-center gap-1 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded px-2 py-1 hover:bg-emerald-100" data-testid="gmail-connected-badge" title={`Αποσύνδεση ${gmailStatus.email}`}>
                <CheckCircle2 className="h-3.5 w-3.5" /> {gmailStatus.email}
              </button>
            ) : (
              <Button variant="outline" size="sm" onClick={handleConnectGmail} className="border-red-200 bg-red-50 text-red-700 hover:bg-red-100" data-testid="gmail-connect-btn">
                <Mail className="h-4 w-4 mr-1" /> Σύνδεση Gmail
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setTemplatesDialogOpen(true)} data-testid="templates-btn" className="bg-amber-50 border-amber-200 hover:bg-amber-100 text-amber-700">
              <LayoutTemplate className="h-4 w-4 mr-1" /> Templates
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAiDialogOpen(true)} data-testid="ai-generate-btn" className="bg-purple-50 border-purple-200 hover:bg-purple-100 text-purple-700" title={`AI generations: ${aiQuota.remaining}/${aiQuota.total} διαθέσιμα`}>
              <Sparkles className="h-4 w-4 mr-1" /> AI <span className="ml-1 bg-purple-100 rounded-full px-1.5 text-xs font-mono">{aiQuota.remaining}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setStyleDialogOpen(true)}>
              <Palette className="h-4 w-4 mr-1" /> Στυλ
            </Button>
            <Button variant="outline" size="sm" onClick={openRecipientsDialog} data-testid="recipients-btn">
              <Users className="h-4 w-4 mr-1" /> Παραλήπτες <span className="ml-1 bg-pink-100 text-pink-700 rounded-full px-2 text-xs">{totalRecipients}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={openUnsubscribesDialog} title="Λίστα Διαγραφών" data-testid="unsubscribes-btn">
              <UserX className="h-4 w-4" />
            </Button>
            {nl.status === 'sending' ? (
              <Button size="sm" onClick={handlePauseSending} disabled={pauseResumeLoading} className="bg-red-600 hover:bg-red-700" data-testid="pause-btn">
                {pauseResumeLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Pause className="h-4 w-4 mr-1" />} Διακοπή Αποστολής
              </Button>
            ) : nl.status === 'paused' ? (
              <Button size="sm" onClick={handleResumeSending} disabled={pauseResumeLoading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="resume-btn">
                {pauseResumeLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />} Συνέχιση ({nl.sent_count || 0}/{nl.total_recipients || totalRecipients})
              </Button>
            ) : (
              <Button size="sm" onClick={handleSend} className="bg-pink-600 hover:bg-pink-700" data-testid="send-btn" disabled={totalRecipients === 0}>
                <Send className="h-4 w-4 mr-1" /> Αποστολή
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Sending progress banner */}
      {(nl.status === 'sending' || nl.status === 'paused' || sendProgress?.status === 'sending') && (
        <div className="max-w-3xl mx-auto px-4 pt-3">
          <div className={`rounded-lg p-3 flex items-center gap-3 border ${nl.status === 'paused' ? 'bg-orange-50 border-orange-200' : 'bg-amber-50 border-amber-200'}`} data-testid="send-progress-banner">
            {nl.status === 'paused' ? <Pause className="h-5 w-5 text-orange-600" /> : <Loader2 className="h-5 w-5 animate-spin text-amber-600" />}
            <div className="flex-1">
              <div className={`text-sm font-semibold ${nl.status === 'paused' ? 'text-orange-800' : 'text-amber-800'}`}>
                {nl.status === 'paused' ? 'Αποστολή σε παύση' : 'Αποστολή σε εξέλιξη...'}
              </div>
              <div className={`text-xs ${nl.status === 'paused' ? 'text-orange-700' : 'text-amber-700'}`}>
                {(sendProgress?.sent_count ?? nl.sent_count ?? 0)} από {(sendProgress?.total_recipients ?? nl.total_recipients ?? 0)} στάλθηκαν
                {(sendProgress?.failed_count ?? nl.failed_count ?? 0) > 0 && ` · ${sendProgress?.failed_count ?? nl.failed_count} σφάλματα`}
                {nl.status === 'paused' && ' · πατήστε "Συνέχιση" για να ολοκληρωθεί'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subject inputs */}
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <div className="bg-white rounded-lg border p-4 shadow-sm space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500 font-semibold">Θέμα Email</Label>
            <Input
              value={nl.subject || ''}
              onChange={(e) => scheduleSave({ subject: e.target.value })}
              placeholder="π.χ. Νέα Προγράμματα ΕΣΠΑ"
              className="text-base font-medium border-0 px-0 focus-visible:ring-0"
              data-testid="nl-subject"
            />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500 font-semibold">Preview Text</Label>
            <Input
              value={nl.preview_text || ''}
              onChange={(e) => scheduleSave({ preview_text: e.target.value })}
              placeholder="Φαίνεται στο inbox δίπλα στο subject"
              className="text-sm text-slate-600 border-0 px-0 focus-visible:ring-0"
            />
          </div>
        </div>
      </div>

      {/* Email canvas - looks like real email */}
      <div className="max-w-3xl mx-auto p-4 pb-24">
        <div className="bg-white rounded-xl shadow-md overflow-hidden border" style={{ fontFamily: nl.style?.font_family || 'Arial, sans-serif' }}>
          {nl.blocks.map((block, idx) => (
            <BlockRenderer
              key={block.block_id}
              block={block}
              primaryColor={primaryColor}
              isUploading={imgUploadLoadingFor === block.block_id}
              onUpdate={(updates) => updateBlock(block.block_id, updates)}
              onRemove={() => removeBlock(block.block_id)}
              onUploadImg={(f) => handleUploadImage(block.block_id, f)}
              onAddBlock={(type) => addBlock(type, idx)}
              draggable
              isDragging={draggedBlockId === block.block_id}
              isDragOver={dragOverBlockId === block.block_id}
              onDragStart={() => handleDragStart(block.block_id)}
              onDragOver={(e) => handleDragOver(e, block.block_id)}
              onDragEnd={handleDragEnd}
              onDrop={(e) => handleDrop(e, block.block_id)}
            />
          ))}
        </div>

        {/* Add block at bottom */}
        <div className="mt-3 flex justify-center gap-2">
          <Button size="sm" variant="outline" onClick={() => addBlock('hero')} data-testid="add-hero"><Plus className="h-3.5 w-3.5 mr-1" /> Hero</Button>
          <Button size="sm" variant="outline" onClick={() => addBlock('text')} data-testid="add-text"><Plus className="h-3.5 w-3.5 mr-1" /> Κείμενο</Button>
          <Button size="sm" variant="outline" onClick={() => addBlock('cta')} data-testid="add-cta"><Plus className="h-3.5 w-3.5 mr-1" /> Κουμπί</Button>
          <Button size="sm" variant="outline" onClick={() => addBlock('footer')} data-testid="add-footer"><Plus className="h-3.5 w-3.5 mr-1" /> Footer</Button>
        </div>
      </div>

      {/* AI Generate Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-purple-600" /> AI Δημιουργία Newsletter</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Label>Τι θέλετε να πείτε στους πελάτες;</Label>
            <Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="π.χ. Ανακοίνωσε τα νέα ΕΣΠΑ προγράμματα για ψηφιακή μετάβαση μικρών επιχειρήσεων" rows={5} data-testid="ai-prompt" />
            <div>
              <Label>Tone</Label>
              <select value={aiTone} onChange={(e) => setAiTone(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
                <option value="professional">Επαγγελματικό</option>
                <option value="friendly">Φιλικό</option>
                <option value="promotional">Προωθητικό</option>
              </select>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded p-3 text-xs text-purple-800">
              ✨ Η AI θα δημιουργήσει επαγγελματικό κείμενο και αυτόματη εικόνα (hero) σχετική με το θέμα.
              <br />
              <strong>Διαθέσιμα AI generations:</strong> {aiQuota.remaining}/{aiQuota.total}
              {aiQuota.remaining <= 0 && <span className="block mt-1 text-red-700">⚠ Έχουν εξαντληθεί. Επικοινωνήστε με τον διαχειριστή.</span>}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAiDialogOpen(false)} disabled={aiLoading}>Άκυρο</Button>
              <Button onClick={handleAIGenerate} disabled={aiLoading} className="bg-purple-600 hover:bg-purple-700" data-testid="ai-generate-confirm">
                {aiLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Δημιουργία...</> : <><Sparkles className="h-4 w-4 mr-1" /> Δημιουργία</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unsubscribes Dialog */}
      <Dialog open={unsubscribesDialogOpen} onOpenChange={setUnsubscribesDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserX className="h-5 w-5 text-red-600" /> Λίστα Διαγραφών (Unsubscribes)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800">
              📌 Αυτοί οι παραλήπτες πάτησαν &quot;Διαγραφή από τη λίστα&quot; σε newsletter που τους στείλατε.
              <br />Δεν θα τους ξανασταλεί κανένα νέο newsletter αυτόματα.
            </div>
            {unsubscribes.length === 0 ? (
              <p className="text-center text-sm text-slate-500 py-8">Κανείς δεν έχει ζητήσει διαγραφή ακόμα ✓</p>
            ) : (
              <div className="border rounded max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="border-b">
                      <th className="text-left p-2 font-semibold">Email</th>
                      <th className="text-left p-2 font-semibold text-xs">Πότε</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unsubscribes.map((u, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="p-2 font-mono text-xs">{u.email}</td>
                        <td className="p-2 text-xs text-slate-500">{u.unsubscribed_at?.slice(0, 19).replace('T', ' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="text-xs text-slate-500">Σύνολο: <strong>{unsubscribes.length}</strong></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog open={templatesDialogOpen} onOpenChange={setTemplatesDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LayoutTemplate className="h-5 w-5 text-amber-600" /> Έτοιμα Newsletter Templates</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
              📌 Τα παρακάτω templates είναι <strong>δωρεάν</strong> — δεν χρεώνονται AI credits. Επιλέξτε ένα και μετά επεξεργαστείτε τα κείμενα.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.map(t => (
                <div key={t.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow flex flex-col" data-testid={`template-${t.id}`}>
                  <img src={t.thumbnail} alt={t.title} className="w-full h-32 object-cover" />
                  <div className="p-3 flex-1 flex flex-col">
                    <h3 className="font-semibold text-sm mb-1" style={{ color: t.primary_color }}>{t.title}</h3>
                    <p className="text-xs text-slate-600 flex-1 mb-2">{t.description}</p>
                    <Button
                      size="sm"
                      onClick={() => handleApplyTemplate(t.id)}
                      disabled={applyingTemplate === t.id}
                      style={{ backgroundColor: t.primary_color }}
                      className="text-white hover:opacity-90"
                      data-testid={`apply-template-${t.id}`}
                    >
                      {applyingTemplate === t.id ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Εφαρμογή...</> : <>Χρήση Template</>}
                    </Button>
                  </div>
                </div>
              ))}
              {templates.length === 0 && <p className="col-span-2 text-center text-sm text-slate-500 py-8">Δεν υπάρχουν διαθέσιμα templates</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recipients Dialog */}
      <Dialog open={recipientsDialogOpen} onOpenChange={setRecipientsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Επιλογή Παραληπτών</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="flex gap-2 flex-wrap">
              <Input value={crmFilter} onChange={(e) => setCrmFilter(e.target.value)} placeholder="Αναζήτηση πελάτη..." className="flex-1 min-w-[200px]" />
              <Button variant="outline" size="sm" onClick={selectAllFiltered}>Επιλογή Όλων</Button>
              <Button variant="outline" size="sm" onClick={deselectAll}>Καθαρισμός</Button>
            </div>
            <div className="text-sm text-slate-600">Επιλεγμένοι: <strong>{selectedCustomerIds.length}</strong></div>
            <div className="border rounded max-h-80 overflow-y-auto">
              {filteredCustomers.map(c => (
                <label key={c.customer_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer border-b last:border-0">
                  <input type="checkbox" checked={selectedCustomerIds.includes(c.customer_id)} onChange={() => toggleCustomer(c.customer_id)} />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{c.company_name}</div>
                    <div className="text-xs text-slate-500">{c.email}</div>
                  </div>
                </label>
              ))}
              {filteredCustomers.length === 0 && <p className="p-4 text-center text-slate-500 text-sm">Δεν βρέθηκαν πελάτες</p>}
            </div>
            <div className="border-t pt-3">
              <Label className="text-sm font-semibold">Manual Emails:</Label>
              <div className="flex gap-2 mt-1">
                <Input value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} placeholder="email@example.com" />
                <Button size="sm" onClick={addManualEmail}>Προσθήκη</Button>
              </div>
              {/* Excel/CSV import */}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => handleImportRecipients(e.target.files?.[0])}
                  data-testid="import-recipients-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => importInputRef.current?.click()}
                  disabled={importingRecipients}
                  data-testid="import-recipients-btn"
                  className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                >
                  {importingRecipients ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />}
                  Εισαγωγή από Excel/CSV
                </Button>
                <span className="text-xs text-slate-500">Αναγνωρίζει στήλη με «email» αυτόματα</span>
              </div>
              {(nl.recipient_emails || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                  {nl.recipient_emails.map(em => (
                    <span key={em} className="text-xs bg-slate-100 rounded px-2 py-0.5 flex items-center gap-1">
                      {em}<button onClick={() => removeManualEmail(em)} className="hover:bg-slate-200 rounded"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setRecipientsDialogOpen(false)}>Άκυρο</Button>
              <Button onClick={saveRecipients} className="bg-pink-600 hover:bg-pink-700">Αποθήκευση</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Style Dialog */}
      <Dialog open={styleDialogOpen} onOpenChange={setStyleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Στυλ Newsletter</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Κύριο Χρώμα (κουμπιά, τίτλοι)</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={primaryColor} onChange={(e) => scheduleSave({ style: { ...nl.style, primary_color: e.target.value } })} className="w-16 h-10 rounded border cursor-pointer" />
                <Input value={primaryColor} onChange={(e) => scheduleSave({ style: { ...nl.style, primary_color: e.target.value } })} className="flex-1 font-mono text-sm" />
              </div>
            </div>
            <div>
              <Label>Logo Επιχείρησης</Label>
              <input
                ref={logoUploadRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleUploadLogo(e.target.files?.[0])}
                data-testid="logo-upload-input"
              />
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => logoUploadRef.current?.click()}
                  disabled={logoUploading}
                  data-testid="logo-upload-btn"
                >
                  {logoUploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                  {nl.style?.logo_url ? 'Αλλαγή Logo' : 'Ανέβασμα Logo'}
                </Button>
                {nl.style?.logo_url && (
                  <Button size="sm" variant="ghost" onClick={() => scheduleSave({ style: { ...nl.style, logo_url: '' } })} className="text-red-600">
                    <X className="h-3.5 w-3.5 mr-1" /> Αφαίρεση
                  </Button>
                )}
              </div>
              {nl.style?.logo_url && (
                <div className="mt-2 p-2 border rounded bg-slate-50">
                  <img src={resolveImg(nl.style.logo_url)} alt="logo" className="max-h-20 mx-auto" />
                </div>
              )}
              <p className="text-xs text-slate-500 mt-1">PNG, JPG ή WebP, έως 5MB</p>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setStyleDialogOpen(false)}>Κλείσιμο</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Block Renderer (WYSIWYG) ----------

const BlockRenderer = ({ block, primaryColor, isUploading, onUpdate, onRemove, onUploadImg, isDragging, isDragOver, onDragStart, onDragOver, onDragEnd, onDrop }) => {
  const uploadRef = useRef(null);
  
  return (
    <div
      className={`relative group border-b last:border-b-0 transition-all ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'border-t-4 border-t-pink-500' : ''}`}
      data-testid={`block-${block.block_type}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
    >
      {/* Hover toolbar */}
      <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 bg-white rounded shadow-md border p-1">
        <span className="p-1.5 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700" title="Μετακινήστε το block">
          <GripVertical className="h-3.5 w-3.5" />
        </span>
        <button onClick={onRemove} className="p-1.5 hover:bg-red-50 rounded" title="Διαγραφή" data-testid={`delete-block-${block.block_id}`}>
          <Trash2 className="h-3.5 w-3.5 text-red-600" />
        </button>
      </div>

      {block.block_type === 'hero' && (
        <div className="relative">
          {/* Image area */}
          <div className="relative bg-slate-100 group/img" style={{ minHeight: block.content.image_url ? 'auto' : '200px' }}>
            {block.content.image_url ? (
              <img src={resolveImg(block.content.image_url)} alt="hero" className="w-full max-h-96 object-cover" />
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <ImageIcon className="h-12 w-12 mb-2" />
                <p className="text-sm">Καμία εικόνα</p>
              </div>
            )}
            {/* Image controls overlay - upload only (AI image is generated once via main AI button) */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 group-hover/img:opacity-100 transition-opacity flex justify-center gap-2">
              <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={(e) => onUploadImg(e.target.files?.[0])} />
              <Button size="sm" variant="secondary" onClick={() => uploadRef.current?.click()} disabled={isUploading}>
                {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                Ανέβασμα εικόνας
              </Button>
              {block.content.image_url && (
                <Button size="sm" variant="secondary" onClick={() => onUpdate({ image_url: '' })}>
                  <X className="h-4 w-4 mr-1" /> Αφαίρεση
                </Button>
              )}
            </div>
          </div>
          {/* Title + subtitle */}
          <div className="p-6 text-center">
            <EditableText
              value={block.content.title || ''}
              onChange={(v) => onUpdate({ title: v })}
              placeholder="Πατήστε για τίτλο..."
              className="text-3xl font-bold leading-tight outline-none focus:bg-yellow-50 px-2 py-1 rounded"
              style={{ color: primaryColor }}
            />
            <EditableText
              value={block.content.subtitle || ''}
              onChange={(v) => onUpdate({ subtitle: v })}
              placeholder="Υπότιτλος (προαιρετικό)"
              className="text-base text-slate-600 mt-2 outline-none focus:bg-yellow-50 px-2 py-1 rounded"
            />
          </div>
        </div>
      )}

      {block.block_type === 'text' && (
        <div className="p-6">
          <EditableText
            value={block.content.heading || ''}
            onChange={(v) => onUpdate({ heading: v })}
            placeholder="Επικεφαλίδα (προαιρετικό)"
            className="text-xl font-semibold mb-3 outline-none focus:bg-yellow-50 px-2 py-1 rounded"
            style={{ color: primaryColor }}
          />
          <EditableMultilineText
            value={block.content.body || ''}
            onChange={(v) => onUpdate({ body: v })}
            placeholder="Γράψτε εδώ το κείμενο σας..."
            className="text-base text-slate-700 leading-relaxed outline-none focus:bg-yellow-50 px-2 py-1 rounded whitespace-pre-wrap"
          />
        </div>
      )}

      {block.block_type === 'cta' && (
        <div className="p-6 text-center">
          <EditableText
            value={block.content.button_text || ''}
            onChange={(v) => onUpdate({ button_text: v })}
            placeholder="Πατήστε εδώ"
            className="inline-block text-white font-bold px-8 py-3 rounded-md outline-none cursor-text"
            style={{ backgroundColor: block.content.button_color || primaryColor }}
          />
          <div className="mt-3 max-w-md mx-auto">
            <Input
              value={block.content.button_url || ''}
              onChange={(e) => onUpdate({ button_url: e.target.value })}
              placeholder="https://..."
              className="text-xs text-center text-slate-500 border-dashed bg-transparent"
            />
          </div>
        </div>
      )}

      {block.block_type === 'footer' && (
        <div className="bg-slate-50 p-6 text-center text-sm text-slate-600 border-t">
          <FooterLogoUploader
            logoUrl={block.content.logo_url}
            onUploaded={(url) => onUpdate({ logo_url: url })}
            onRemoved={() => onUpdate({ logo_url: '' })}
          />
          <EditableText
            value={block.content.text || ''}
            onChange={(v) => onUpdate({ text: v })}
            placeholder="Κλείσιμο..."
            className="outline-none focus:bg-yellow-50 px-2 py-1 rounded"
          />
          <EditableText
            value={block.content.signature || ''}
            onChange={(v) => onUpdate({ signature: v })}
            placeholder="Υπογραφή / Όνομα Εταιρείας"
            className="font-semibold text-slate-800 mt-1 outline-none focus:bg-yellow-50 px-2 py-1 rounded"
          />
        </div>
      )}
    </div>
  );
};

// Footer logo uploader (uses generic block image upload endpoint for the newsletter)
const FooterLogoUploader = ({ logoUrl, onUploaded, onRemoved }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Resolve url from path params via window — keep it simple
  const handle = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Παρακαλώ επιλέξτε εικόνα'); return; }
    setUploading(true);
    try {
      const pathParts = window.location.pathname.split('/');
      const nlId = pathParts[pathParts.length - 1];
      // Try to read user_id from localStorage 'user'
      const userRaw = localStorage.getItem('user');
      const userId = userRaw ? (JSON.parse(userRaw).user_id || '') : '';
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(`${API}/newsletter/drafts/${nlId}/upload-image?user_id=${userId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onUploaded(r.data.image_url);
      toast.success('Logo ανέβηκε');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Σφάλμα ανεβάσματος');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  return (
    <div className="mb-3 flex flex-col items-center gap-2">
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handle(e.target.files?.[0])} />
      {logoUrl ? (
        <>
          <img src={resolveImg(logoUrl)} alt="logo" className="max-h-12 mx-auto" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
              Αλλαγή Logo
            </Button>
            <Button size="sm" variant="ghost" onClick={onRemoved} className="text-red-600 hover:bg-red-50">
              <X className="h-3.5 w-3.5 mr-1" /> Αφαίρεση
            </Button>
          </div>
        </>
      ) : (
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="footer-logo-upload">
          {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
          Ανέβασμα Logo
        </Button>
      )}
    </div>
  );
};

// Single-line contentEditable
const EditableText = ({ value, onChange, placeholder, className, style }) => {
  const ref = useRef(null);
  // Only update DOM if value changed externally (avoid cursor jump)
  useEffect(() => {
    if (ref.current && ref.current.innerText !== (value || '')) {
      ref.current.innerText = value || '';
    }
  }, [value]);
  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      className={`${className || ''} editable-text`}
      style={style}
      onBlur={(e) => onChange(e.currentTarget.innerText)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
    />
  );
};

// Multi-line contentEditable
const EditableMultilineText = ({ value, onChange, placeholder, className }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerText !== (value || '')) {
      ref.current.innerText = value || '';
    }
  }, [value]);
  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      className={`${className || ''} editable-text min-h-[80px]`}
      onBlur={(e) => onChange(e.currentTarget.innerText)}
    />
  );
};

export default NewsletterEditor;
